import React, { useCallback } from 'react';
import classnames from 'classnames';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import { Trans } from 'react-i18next';
import {
  PlanPriceDetails,
  Button,
  useToast,
  PlanBillingSummary,
} from 'components';
import {
  Plan,
  useGenerateQuoteFromPlan,
  Session,
  openClientQuoteDocument,
  useCreditBalance,
} from '@app/api';
import { usePlanPriceTotal } from 'lib/priceComputation';

const useStyles = createUseStyles<string, {}, Theme>((theme: Theme) => ({
  reviewPlanCard: {
    borderRadius: theme.borderRadius.std,
    boxShadow: theme.boxShadow.base,
    backgroundColor: theme.colors.white,
    marginBottom: theme.marginBase * 8,

    [theme.media.tablet]: {
      position: 'relative',
      width: '100%',
    },
  },

  header: {
    padding: theme.marginBase * 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: {
    ...theme.fonts.h4,
  },
  prices: {
    padding: theme.marginBase * 2,
    backgroundColor: theme.colors.grayBackground,
  },
  openModalButton: {
    ...theme.fonts.label,
    color: theme.colors.blueAccessible,
  },
  billingInfo: {
    backgroundColor: theme.colors.white,
    padding: [[theme.marginBase * 2, theme.marginBase * 2]],
  },
}));

interface Props {
  plan: Plan;
  session: Session;
  isBillingInformationRequired?: boolean;
  className?: string;
  children?: React.ReactNode;
}

export const ReviewPlanCard = ({
  plan,
  session,
  className,
  children,
  isBillingInformationRequired,
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });

  const toast = useToast();
  const { data: creditBalance } = useCreditBalance(session.organizationId);
  const { mutateAsync: generateQuote } = useGenerateQuoteFromPlan(
    session.planId,
  );
  const priceTotals = usePlanPriceTotal(session, plan);

  const onDownloadQuoteClick = useCallback(async () => {
    try {
      const quote = await generateQuote(session);
      openClientQuoteDocument(quote.ref ?? quote.id);
    } catch (e) {
      toast.exception(e);
    }
  }, [session, toast, generateQuote]);

  return (
    <div className={classnames(className, classes.reviewPlanCard)}>
      <div className={classes.header}>
        <h2 className={classes.title}>
          <Trans i18nKey="billing.studyPrice.title" />
        </h2>
        <Button
          variant="transparent"
          color="accent"
          onClick={onDownloadQuoteClick}
          small
          target="_blank"
        >
          <Trans i18nKey="billing.studyPrice.download" />
        </Button>
      </div>
      <PlanPriceDetails
        className={classes.prices}
        plan={plan}
        session={session}
        defaultOpened={false}
      />

      <div className={classes.billingInfo}>
        <PlanBillingSummary
          session={session}
          isBillingInformationRequired={isBillingInformationRequired}
          priceTotals={priceTotals}
          creditBalance={creditBalance}
        />
        {children}
      </div>
    </div>
  );
};
